import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { servicesData } from '../../mocks/services';

const ServiceDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const service = servicesData.find((s) => s.slug === slug);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Service non trouvé</h1>
          <Link to="/nos-services" className="text-primary hover:underline cursor-pointer">
            Retour aux services
          </Link>
        </div>
      </div>
    );
  }

  const relatedServices = servicesData.filter((s) => s.id !== service.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <WhatsAppButton />

      {/* Hero Section */}
      <section className="relative pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link
              to="/nos-services"
              className="inline-flex items-center space-x-2 text-primary hover:text-primary-dark mb-6 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              <span>Retour aux services</span>
            </Link>
            <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <i className={`${service.icon} text-primary`}></i>
              <span className="text-sm font-medium text-primary">Service professionnel</span>
            </div>
            <h1 className="font-serif text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
              {service.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Hero Image */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="rounded-3xl overflow-hidden shadow-2xl"
              >
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-[400px] object-cover object-top"
                />
              </motion.div>

              {/* Description */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-serif text-3xl font-bold text-gray-900 mb-4">
                  À propos de ce service
                </h2>
                <p className="text-gray-700 leading-relaxed text-lg">
                  {service.fullDescription}
                </p>
              </motion.div>

              {/* Benefits */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-gray-50 rounded-3xl p-8"
              >
                <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">
                  Bienfaits
                </h2>
                <ul className="space-y-4">
                  {service.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <div className="w-6 h-6 flex items-center justify-center bg-primary rounded-full flex-shrink-0 mt-1">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700 leading-relaxed">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Process */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">
                  Déroulement
                </h2>
                <div className="space-y-6">
                  {service.process.map((step, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary text-white rounded-full font-bold flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1 pt-2">
                        <p className="text-gray-700 leading-relaxed">{step}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Learn More Button - Only for Massotherapy Service */}
              {service.slug === 'massotherapie-professionnelle' && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="flex justify-center"
                >
                  <a
                    href="https://taplink.cc/hmcclinicmassagepro"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center space-x-3 bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary text-white px-8 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-information-line text-2xl"></i>
                    <span>En savoir plus sur ce service</span>
                    <i className="ri-arrow-right-line text-xl"></i>
                  </a>
                </motion.div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Booking Card */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="bg-gradient-to-br from-primary to-primary-dark text-white rounded-3xl p-8 shadow-2xl"
                >
                  <h3 className="font-serif text-2xl font-bold mb-6">Réserver ce service</h3>
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center justify-between pb-4 border-b border-white/20">
                      <span className="text-white/80">Durée</span>
                      <span className="font-semibold">{service.duration}</span>
                    </div>
                    <div className="flex items-center justify-between pb-4 border-b border-white/20">
                      <span className="text-white/80">Tarif</span>
                      <span className="font-semibold">{service.price}</span>
                    </div>
                  </div>
                  <a
                    href="https://wa.me/243850465500"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center space-x-2 bg-accent hover:bg-accent-dark text-white px-6 py-4 rounded-full font-semibold transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line text-xl"></i>
                    <span>Prendre RDV</span>
                  </a>
                  <p className="text-white/70 text-sm text-center mt-4">
                    Réponse sous 2h via WhatsApp
                  </p>
                </motion.div>

                {/* Related Services */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="bg-gray-50 rounded-3xl p-8"
                >
                  <h3 className="font-serif text-xl font-bold text-gray-900 mb-6">
                    Services connexes
                  </h3>
                  <div className="space-y-4">
                    {relatedServices.map((relatedService) => (
                      <Link
                        key={relatedService.id}
                        to={`/service/${relatedService.slug}`}
                        className="flex items-center space-x-3 p-3 bg-white rounded-xl hover:shadow-md transition-all cursor-pointer group"
                      >
                        <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                          <i className={`${relatedService.icon} text-xl text-primary`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 text-sm line-clamp-2 group-hover:text-primary transition-colors">
                            {relatedService.title}
                          </div>
                        </div>
                        <i className="ri-arrow-right-line text-gray-400 group-hover:text-primary transition-colors"></i>
                      </Link>
                    ))}
                  </div>
                </motion.div>

                {/* Contact Info */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  className="bg-white border-2 border-gray-200 rounded-3xl p-8"
                >
                  <h3 className="font-serif text-xl font-bold text-gray-900 mb-6">
                    Besoin d'aide ?
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <i className="ri-phone-line text-xl text-primary mt-1"></i>
                      <div>
                        <div className="text-sm text-gray-600 mb-1">Téléphone</div>
                        <a href="tel:+243850465500" className="font-medium text-gray-900 hover:text-primary cursor-pointer">
                          +243 850 465 500
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-mail-line text-xl text-primary mt-1"></i>
                      <div>
                        <div className="text-sm text-gray-600 mb-1">Email</div>
                        <a href="mailto:hmcclinicrdc@gmail.com" className="font-medium text-gray-900 hover:text-primary cursor-pointer">
                          hmcclinicrdc@gmail.com
                        </a>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceDetailPage;